import React, { useState, useEffect, useMemo } from 'react';
import { Users, TrendingUp, TrendingDown, Check, Edit2, X, AlertCircle, RefreshCw, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { API_URL } from '../config';
import SearchBox from './shared/SearchBox';

export default function ModulationManagement({ authToken }) {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    if (authToken) {
      loadEmployees();
    }
  }, [authToken]);

  const loadEmployees = async () => {
    if (!authToken) {
      setError('Authentification requise');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/modulation`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEmployees(data);
      } else {
        const errData = await response.json().catch(() => ({}));
        setError(`Erreur ${response.status}: ${errData.error || response.statusText}`);
      }
    } catch (err) {
      console.error('Erreur chargement employés:', err);
      setError('Erreur réseau: ' + err.message);
    }
    setLoading(false);
  };

  const handleEdit = (emp) => {
    setEditingId(emp.id);
    setEditValue((emp.compteur_modulation || 0).toFixed(2));
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditValue('');
  };

  const handleSave = async (employeeId) => {
    setUpdating(true);
    try {
      const response = await fetch(`${API_URL}/modulation/${employeeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ compteur_modulation: parseFloat(editValue) })
      });

      if (response.ok) {
        await loadEmployees();
        setEditingId(null);
        setEditValue('');
      }
    } catch (err) {
      console.error('Erreur mise à jour compteur:', err);
    }
    setUpdating(false);
  };

  const updateAllCounters = async () => {
    if (!window.confirm('Mettre à jour tous les compteurs pour le mois écoulé ?')) return;
    
    setUpdating(true);
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
    
    try {
      const response = await fetch(`${API_URL}/modulation/update-all`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          year: lastMonth.getFullYear(),
          month: lastMonth.getMonth() + 1
        })
      });

      if (response.ok) {
        const data = await response.json();
        alert(`✅ ${data.processedUsers} compteurs mis à jour`);
        await loadEmployees();
      }
    } catch (err) {
      console.error('Erreur mise à jour globale:', err);
      alert('❌ Erreur lors de la mise à jour');
    }
    setUpdating(false);
  };

  const formatHeures = (heures) => {
    const h = Math.floor(Math.abs(heures));
    const m = Math.round((Math.abs(heures) - h) * 60);
    const sign = heures >= 0 ? '+' : '-';
    return `${sign}${h}h${m.toString().padStart(2, '0')}`;
  };

  const getCompteurColor = (compteur) => {
    if (compteur > 0) return 'text-green-600 dark:text-green-400';
    if (compteur < 0) return 'text-red-600 dark:text-red-400';
    return 'text-text-secondary';
  };

  const employeesWithoutContract = employees.filter(e => !e.heures_hebdomadaires);
  const allEmployeesWithContract = employees.filter(e => e.heures_hebdomadaires);

  // Recherche + tri appliqués au tableau principal
  const employeesWithContract = useMemo(() => {
    let arr = allEmployeesWithContract;
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(e =>
        (`${e.first_name} ${e.last_name}` || '').toLowerCase().includes(q) ||
        (e.role_name || '').toLowerCase().includes(q)
      );
    }
    const sorted = [...arr].sort((a, b) => {
      let va, vb;
      if (sortKey === 'name') { va = `${a.last_name} ${a.first_name}`; vb = `${b.last_name} ${b.first_name}`; }
      else if (sortKey === 'role') { va = a.role_name || ''; vb = b.role_name || ''; }
      else if (sortKey === 'heures') { va = parseFloat(a.heures_hebdomadaires) || 0; vb = parseFloat(b.heures_hebdomadaires) || 0; }
      else if (sortKey === 'compteur') { va = parseFloat(a.compteur_modulation) || 0; vb = parseFloat(b.compteur_modulation) || 0; }
      else { va = ''; vb = ''; }
      if (typeof va === 'number') return sortDir === 'asc' ? va - vb : vb - va;
      const cmp = String(va).localeCompare(String(vb), 'fr', { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return sorted;
  }, [allEmployeesWithContract, search, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ k }) => {
    if (sortKey !== k) return <ChevronsUpDown className="w-3 h-3 opacity-40 inline-block" aria-hidden="true" />;
    return sortDir === 'asc'
      ? <ChevronUp className="w-3 h-3 inline-block" aria-hidden="true" />
      : <ChevronDown className="w-3 h-3 inline-block" aria-hidden="true" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-surface rounded-xl shadow-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Users className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
            <div>
              <h2 className="text-2xl font-bold text-text-primary">Gestion de la modulation</h2>
              <p className="text-text-secondary text-sm">
                {employeesWithContract.length} employés avec contrat configuré
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={loadEmployees}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-elevated-1 hover:bg-elevated-1 text-text-secondary rounded-lg font-medium transition disabled:opacity-50"
              title="Rafraîchir"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Rafraîchir
            </button>
            <button
              onClick={updateAllCounters}
              disabled={updating}
              className="flex items-center gap-2 px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-5 h-5 ${updating ? 'animate-spin' : ''}`} />
              Mettre à jour tous les compteurs
            </button>
          </div>
        </div>
      </div>

      {/* Auth error */}
      {!authToken && (
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <p className="font-semibold text-red-800">Authentification requise. Veuillez vous reconnecter.</p>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-950/40 border border-red-200 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
            <p className="font-semibold text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Alertes */}
      {employeesWithoutContract.length > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-950/40 border border-yellow-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-yellow-800">
                {employeesWithoutContract.length} employé(s) sans contrat configuré
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                Ces employés n'ont pas d'heures hebdomadaires définies. 
                Configurez leurs contrats dans la section "Employés".
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {employeesWithoutContract.map(emp => (
                  <span key={emp.id} className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-200 rounded-full text-sm">
                    {emp.first_name} {emp.last_name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Barre de recherche */}
      <div className="bg-surface rounded-xl shadow-lg p-4">
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <div className="flex-1">
            <SearchBox value={search} onChange={setSearch} placeholder="Rechercher un employé (nom, rôle)..." size="md" />
          </div>
          <span className="text-sm text-text-muted whitespace-nowrap">
            {employeesWithContract.length} / {allEmployeesWithContract.length} affichés
          </span>
        </div>
      </div>

      {/* Table des employés */}
      <div className="bg-surface rounded-xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left font-semibold cursor-pointer select-none hover:bg-white/10 transition" onClick={() => toggleSort('name')}>
                  Employé <SortIcon k="name" />
                </th>
                <th className="px-6 py-4 text-center font-semibold cursor-pointer select-none hover:bg-white/10 transition" onClick={() => toggleSort('role')}>
                  Rôle <SortIcon k="role" />
                </th>
                <th className="px-6 py-4 text-center font-semibold cursor-pointer select-none hover:bg-white/10 transition" onClick={() => toggleSort('heures')}>
                  Contrat <SortIcon k="heures" />
                </th>
                <th className="px-6 py-4 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition" onClick={() => toggleSort('compteur')}>
                  Compteur actuel <SortIcon k="compteur" />
                </th>
                <th className="px-6 py-4 text-center font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-text-muted">
                    Chargement...
                  </td>
                </tr>
              ) : employeesWithContract.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-text-muted">
                    Aucun employé avec contrat configuré
                  </td>
                </tr>
              ) : (
                employeesWithContract.map(emp => (
                  <tr key={emp.id} className="hover:bg-elevated-1 transition">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-text-primary">
                        {emp.first_name} {emp.last_name}
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 text-center">
                      <span className="px-3 py-1 bg-elevated-1 text-text-secondary rounded-full text-sm">
                        {emp.role_name}
                      </span>
                    </td>
                    
                    <td className="px-6 py-4 text-center">
                      <span className="font-semibold text-text-secondary">
                        {emp.heures_hebdomadaires}h/sem
                      </span>
                    </td>
                    
                    <td className="px-6 py-4 text-right">
                      {editingId === emp.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <input
                            type="number"
                            step="0.01"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-24 px-3 py-1 border border-border-strong rounded text-center focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            autoFocus
                          />
                          <span className="text-text-secondary">h</span>
                        </div>
                      ) : (
                        <div className="flex items-center justify-end gap-2">
                          {emp.compteur_modulation > 0 && <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />}
                          {emp.compteur_modulation < 0 && <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400" />}
                          {emp.compteur_modulation === 0 && <Check className="w-4 h-4 text-text-muted" />}
                          <span className={`text-xl font-bold ${getCompteurColor(emp.compteur_modulation || 0)}`}>
                            {formatHeures(emp.compteur_modulation || 0)}
                          </span>
                        </div>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 text-center">
                      {editingId === emp.id ? (
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleSave(emp.id)}
                            disabled={updating}
                            className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition disabled:opacity-50"
                            title="Enregistrer"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleCancel}
                            className="p-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition"
                            title="Annuler"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(emp)}
                          className="p-2 bg-indigo-500 hover:bg-indigo-600 text-white rounded-lg transition"
                          title="Modifier"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
